import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { streamRun } from '../api/sse';
import { useApi, useTokenGetter } from '../hooks/useApi';
import type { ContentBlock, MessageOut } from '../types/api';
import { useConversations } from './ConversationsContext';

export type RunState = 'idle' | 'sending' | 'streaming' | 'error';

export interface PendingAssistant {
  // index → block. We render in numeric-ascending order.
  blocks: Record<number, ContentBlock>;
  runId: string | null;
}

interface ActiveConversationContextValue {
  conversationId: string | null;
  messages: MessageOut[];
  loading: boolean;
  loadError: string | null;
  runState: RunState;
  runError: string | null;
  pending: PendingAssistant | null;
  send: (text: string) => Promise<void>;
  stop: () => void;
}

const ActiveConversationContext = createContext<ActiveConversationContextValue | null>(null);

interface ProviderProps {
  /** Route param: either 'new' (lazy create on first send) or a UUID. */
  paramId: string;
  children: ReactNode;
}

export function ActiveConversationProvider({ paramId, children }: ProviderProps) {
  const navigate = useNavigate();
  const api = useApi();
  const getToken = useTokenGetter();
  const { create, upsertSummary } = useConversations();
  const { t } = useTranslation();

  const isNew = paramId === 'new';

  const [conversationId, setConversationId] = useState<string | null>(isNew ? null : paramId);
  const [messages, setMessages] = useState<MessageOut[]>([]);
  const [loading, setLoading] = useState<boolean>(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [runState, setRunState] = useState<RunState>('idle');
  const [runError, setRunError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAssistant | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  // Synchronous mirror of `pending`. State updaters must be pure (StrictMode
  // calls them twice in dev), so we read/write through the ref and only call
  // setPending(pendingRef.current) for re-render purposes.
  const pendingRef = useRef<PendingAssistant | null>(null);

  // Initial load for an existing conversation.
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api
        .getConversation(paramId)
        .then((conv) => {
          if (cancelled) return;
          setMessages(conv.messages);
          setLoading(false);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setLoadError(e instanceof Error ? e.message : 'Failed to load conversation');
          setLoading(false);
        });
    return () => {
      cancelled = true;
    };
  }, [paramId, isNew, api]);

  // Cleanup: abort any in-flight stream on unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const send = useCallback(
      async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        if (runState !== 'idle' && runState !== 'error') return;

        setRunState('sending');
        setRunError(null);

        try {
          // Lazy-create conversation if needed.
          let cid = conversationId;
          if (cid === null) {
            const conv = await create(null);
            cid = conv.id;
            setConversationId(cid);
            // Sidebar gets the canonical row.
            upsertSummary({
              id: conv.id,
              title: conv.title,
              app_id: conv.app_id,
              archived_at: conv.archived_at,
              created_at: conv.created_at,
              updated_at: conv.updated_at,
            });
          }

          // Append user message.
          const userMsg = await api.appendMessage(cid, {
            content: [{ type: 'text', text: trimmed }],
          });
          setMessages((prev) => [...prev, userMsg]);

          // Open SSE run.
          setRunState('streaming');
          pendingRef.current = { blocks: {}, runId: null };
          setPending(pendingRef.current);

          const ac = new AbortController();
          abortRef.current = ac;

          const token = await getToken();

          await streamRun(
              cid,
              token,
              {
                onRunStart: (data) => {
                  pendingRef.current = pendingRef.current
                      ? { ...pendingRef.current, runId: data.run_id }
                      : { blocks: {}, runId: data.run_id };
                  setPending(pendingRef.current);
                },
                onBlockStart: (data) => {
                  if (!pendingRef.current) return;
                  const block: ContentBlock =
                      data.block.type === 'text'
                          ? { type: 'text', text: '' }
                          : (data.block as ContentBlock);
                  pendingRef.current = {
                    ...pendingRef.current,
                    blocks: { ...pendingRef.current.blocks, [data.index]: block },
                  };
                  setPending(pendingRef.current);
                },
                onBlockDelta: (data) => {
                  const p = pendingRef.current;
                  if (!p) return;
                  const existing = p.blocks[data.index];
                  if (!existing || existing.type !== 'text') return;
                  const updated: ContentBlock = {
                    ...existing,
                    text: (existing as { text: string }).text + data.delta.text,
                  };
                  pendingRef.current = {
                    ...p,
                    blocks: { ...p.blocks, [data.index]: updated },
                  };
                  setPending(pendingRef.current);
                },
                onBlockStop: () => {
                  // No-op: text blocks already accumulated; non-text blocks complete on block_start.
                },
                onRunFinish: (data) => {
                  const p = pendingRef.current;
                  if (p) {
                    const indices = Object.keys(p.blocks)
                        .map(Number)
                        .sort((a, b) => a - b);
                    const content = indices
                        .map((i) => p.blocks[i])
                        .filter((b): b is ContentBlock => b !== undefined);

                    const assistantMsg: MessageOut = {
                      id: `local-${data.run_id}`,
                      role: 'assistant',
                      turn_index: -1,
                      content,
                      run_id: data.run_id,
                      created_at: new Date().toISOString(),
                    };
                    setMessages((prev) => [...prev, assistantMsg]);
                  }
                  pendingRef.current = null;
                  setPending(null);

                  if (data.status === 'complete') {
                    setRunState('idle');
                  } else {
                    setRunState('error');
                    setRunError(t('errorRunFailed'));
                  }

                  // Lazy-create flow: if URL is still /c/new, swap to canonical id.
                  if (isNew && cid) {
                    navigate(`/c/${cid}`, { replace: true });
                  }
                },
                onError: (data) => {
                  setRunError(data.message || t('errorGeneric'));
                  setRunState('error');
                  pendingRef.current = null;
                  setPending(null);
                },
              },
              ac.signal,
          );
        } catch (e) {
          const err = e as Error;
          if (err.name === 'AbortError') {
            // User stopped the stream. Backend continues; we just close UI state.
            setRunState('idle');
            pendingRef.current = null;
            setPending(null);
          } else {
            setRunError(err.message || t('errorGeneric'));
            setRunState('error');
            pendingRef.current = null;
            setPending(null);
          }
        } finally {
          abortRef.current = null;
        }
      },
      [api, conversationId, create, getToken, isNew, navigate, runState, t, upsertSummary],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const value = useMemo<ActiveConversationContextValue>(
      () => ({
        conversationId,
        messages,
        loading,
        loadError,
        runState,
        runError,
        pending,
        send,
        stop,
      }),
      [conversationId, messages, loading, loadError, runState, runError, pending, send, stop],
  );

  return (
      <ActiveConversationContext.Provider value={value}>
        {children}
      </ActiveConversationContext.Provider>
  );
}

export function useActiveConversation(): ActiveConversationContextValue {
  const v = useContext(ActiveConversationContext);
  if (!v) throw new Error('useActiveConversation must be used inside ActiveConversationProvider');
  return v;
}
